<script>
  import { createEventDispatcher } from 'svelte';
  import { user, tags } from '../stores.js';
  import Modal from './Modal.svelte';
  import Button from './Button.svelte';
  import TagsInput from './TagsInput.svelte';
  import TextField2 from './TextField2.svelte';
  import ComboBox2 from './ComboBox2.svelte';
  import Checkbox from './Checkbox.svelte';
  import NumericInput from './NumericInput.svelte';
  import LoadingAnimation from './LoadingAnimation.svelte';
  import Notification from './Notification.svelte';
  import QueryResultViewer from './QueryResultViewer.svelte';
  import { upsertComponentQuery, getTags, testQuery } from '../api';

  const dispatch = createEventDispatcher();

  let defaultQuery = { Interval: "48 hours" };
  let edit = false;
  let oldInHouseStock;
  let oldComments;
  let oldAttention;
  let notification;
  let queryNameElement;
  let queryResultsViewer;
  let visible = false;
  let okButtonText = "";
  let query = defaultQuery;
  let isTesting = false;
  let isSubmitting = false;
  let nameError = "";
  let partIDsError = "";
  let queryError = "";
  let queryTags;

  const queryIntervals = [ "6 hours", "48 hours", "1 week" ];

  export async function show(queryToEdit) {
    isTesting = false;
    isSubmitting = false;
    edit = !!queryToEdit;
    if (edit) oldInHouseStock = queryToEdit.InHouseStock;
    if (edit) oldComments = queryToEdit.Comments;
    if (edit) oldAttention = queryToEdit.Attention;
    okButtonText = edit ? "Save" : "Add";
    query = queryToEdit ? {...queryToEdit } : { ...defaultQuery };
    nameError = "";
    queryError = "";
    queryTags = query.Tags?.length > 0 ? query.Tags?.split(",") : [];
       
    const response = await getTags();
    if (response.ok) {
      $tags = response.data;
    }

    visible = true;
    queryNameElement.focus();
  }

  function onTags(e) {
    query.Tags = e.detail.tags.join(",");
  }

  function validate() {
    let valid = true;
    if (query.Name === "") { 
      valid = false;
      nameError = "Cannot be empty";
    }

    if (query.Type === "Lookup" && query.LookupPartIDs === "") { 
      valid = false;
      partIDsError = "Cannot be empty";
    }

    if (query.Type === "Search" && query.SearchMpnQuery === "") { 
      valid = false;
      queryError = "Cannot be empty";
    }

    return valid;
  }

  async function test() {
    if (!validate()) return;

    isTesting = true;
    const response = await testQuery(query, 10);
    isTesting = false;
    if (response.error) {
      notification.show("error", response.error);
    } else {
      if (response.data.length > 0) queryResultsViewer.show(response.data);
      else notification.show("warning", "No results");
    }
  }

  async function save() {
    if (!validate()) return;

    isSubmitting = true;

    if (!edit || (oldInHouseStock !== query.InHouseStock)) {
      query.InHouseStockDate = dayjs().format("YYYY-MM-DD");
    }
    if (!edit || (oldComments !== query.Comments)) {
      query.CommentsDate = dayjs().format("YYYY-MM-DD");
    }
    if (!edit || (oldAttention !== query.Attention)) {
      query.AttentionDate = dayjs().format("YYYY-MM-DD");
    }
    query.LastEditedBy = $user?.userDetails || "?";
    const {ok, error, status, data} = await upsertComponentQuery(query);
    dispatch('saved', {ok, error, status, data, edit});
    visible = false;
  }
</script>

<Modal bind:visible>
  <div class="container">
    <TextField2 label="Name" bind:this={queryNameElement} bind:text={query.Name} bind:errorText={nameError} clearErrorOnEdit width="100%" />
    <TextField2 label="AX No" bind:text={query.AxNo} />
    <TagsInput label="Tags" tags={queryTags} autocompleteTags={$tags} on:tags={onTags} helpText="Press enter/TAB to create tag (ex: DSPII, PSU Board)" />
    <TextField2 label="Search Query" bind:text={query.SearchQuery} helpText="Keywords separated by spaces. Keywords can be MPN/parts of MPN, package, values, etc. Examples: 'BAV99W SOT-323', 'cap 2.2uF 10% 100V'. Wildcards are not supported. Hence if you want to match components with part no LT3999*DD, you need to do broader search, e.g. LT3999 and use MPN Filter (e.g. LT3999*DD) to filter MPNs of returned components." bind:errorText={queryError} clearErrorOnEdit />
    <TextField2 label="MPN Filter" bind:text={query.MpnFilter} bind:errorText={queryError} clearErrorOnEdit />
    <TextField2 label="Comments" bind:text={query.Comments} />
    <TextField2 label="Attention" bind:text={query.Attention} helpText="Will make this component always display in the report" />
    <NumericInput label="Last Order" bind:value={query.InHouseStock} />
    <ComboBox2 label="Query Interval" placeholder="" items={queryIntervals} bind:selectedItem={query.Interval} dropDownOnly />
    <Checkbox label="Notify about every quantity increase" bind:checked={query.NotifyEveryIncrease} />
    <div class="buttons">
      <Button secondary title="Test Query" on:click={test} disabled={isSubmitting || isTesting}>
        {#if isTesting}
          <LoadingAnimation count={4} dotSize={6} gap={2} visible />
        {:else}
          Test
        {/if}
      </Button>
      <Button secondary title="Discard" on:click={() => visible = false} disabled={isSubmitting || isTesting}>Discard</Button>
      <Button on:click={save} title={okButtonText} disabled={isSubmitting || isTesting}>
        {#if isSubmitting}
          <LoadingAnimation count={4} dotSize={6} gap={2} visible />
        {:else}
          {okButtonText}
        {/if}
      </Button>
    </div>
  </div>
</Modal>
<QueryResultViewer bind:this={queryResultsViewer} />
<Notification bind:this={notification} />

<style>
  .container {
    display: grid;
    grid-auto-flow: row;
    justify-items: stretch;
    align-items: center;
    gap: 8px;
    margin: 0;
    width: 400px;
  }

  .buttons {
    margin: 10px 0 0 0;
    display: grid;
    grid-template-columns: auto min-content min-content;
    justify-self: stretch;
    gap: 10px;
  }
</style>
